<template>
  <div class="coupon-list">
    <div class="animated fadeIn">
      <b-row>
        <b-col cols="12">
          <b-card
            header-tag="header"
            footer-tag="footer">
            <div slot="header">
              <i className="fa fa-align-justify"></i><strong class="text-uppercase">{{ $t('coupon.child_coupon') }}</strong>
              <div class="pull-right mr-4">
                <button type="button" class="btn btn-success right" @click="createChildCoupon">
                  <i class="fa fa-plus"></i>&nbsp; {{$t('coupon.new_child_coupon')}}</button>
              </div>
            </div>
            <b-col cols="12" class="text-center">
              <!-- Start of Filter session -->
              <b-row class="mb-3">
                <b-col xl="12">
                  <div class="pull-right">
                    <b-input-group v-bind:prepend="$t('coupon.applicable_period')">
                      <date-range-picker ref="drange" :startDate="filter.startAt" :endDate="filter.endAt" @update="updateDateRange" opens="left" :locale-data="date_locale" :ranges="ranges"></date-range-picker>
                    </b-input-group>
                  </div>
                  <div class="pull-right mr-2">
                    <button type="button" class="btn bg-main border-radius-4px" @click="onValidFilter"><i class="fa fa-filter"></i> {{$t('coupon.on_valid')}}</button>
                  </div>
                  <div class="pull-right mr-2">
                    <a class="btn btn-secondary border-radius-4px" href="#/child-coupon/import-history">{{$t('coupon.import_history')}}</a>
                  </div>
                </b-col>
              </b-row>

              <b-row>
                <b-col xl="6">
                  <div class="pull-right mr-2">
                    <b-input-group v-bind:prepend="$t('common.active')">
                      <b-form-select v-model="filter.active" v-bind:value="filter.active"
                      :options="statuses[$session.get('lang')]" v-bind:placeholder="$t('common.active')">
                      </b-form-select>
                    </b-input-group>
                  </div>
                  <div class="pull-right mr-2">
                    <b-input-group v-bind:prepend="$t('coupon.coupon_type')">
                      <b-form-select v-model="filter.coupon_type" v-bind:value="filter.coupon_type"
                      :options="couponTypes[$session.get('lang')]" v-bind:placeholder="$t('coupon.coupon_type')">
                      </b-form-select>
                    </b-input-group>
                  </div>
                  <div class="pull-right mr-2">
                    <b-input-group v-bind:prepend="$t('coupon.auto_apply')">
                      <b-form-select v-model="filter.auto_apply" v-bind:value="filter.auto_apply"
                      :options="auto_apply[$session.get('lang')]" v-bind:placeholder="$t('coupon.auto_apply')">
                      </b-form-select>
                    </b-input-group>
                  </div>
                </b-col>
                <b-col xl="6">
                  <b-input-group>
                    <select-dropdown v-bind:options="filter.options" v-bind:selected="filter.selected"
                    position="prepend" v-bind:all="false" v-model="filter.field" prefix="coupon">
                    </select-dropdown>
                    <input type="text" class="form-control" v-model="filter.key" v-on:keydown.enter="refreshData"/>
                    <div class="input-group-append">
                      <b-input-group-append>
                        <b-btn @click="refreshData()"><i class="fa fa-search" ></i> {{$t('common.search')}}</b-btn>
                      </b-input-group-append>
                    </div>
                  </b-input-group>
                </b-col>
              </b-row>
              <!-- End of Filter session -->

              <!-- Start of Data table session -->
              <div class="table-responsive single-data">
                <table class="table table-striped table-hover table-sm table-bordered">
                  <thead>
                  <tr>
                    <th scope="col">{{$t('common.active')}}</th>
                    <th scope="col" style="min-width: 150px" v-for="item in dataTable.fields" :key="item.id" @click="updateSort(item.name)" :class="item.sort?'pointer':''">
                      <i class="fa fa-sort-amount-asc text-muted"
                      v-if="dataTable.sort.type == 'asc' && dataTable.sort.field == item.name"></i>
                      <i class="fa fa-sort-amount-desc text-muted"
                      v-if="dataTable.sort.type == 'desc' && dataTable.sort.field == item.name"></i>
                      <span v-if="item.label != null">{{$t('coupon.'+item.label)}}</span>
                      <span v-if="item.label == null">{{$t('coupon.'+item.name)}}</span>
                    </th>
                    <th scope="col">{{$t('common.action')}}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-if="dataTable.items.length == 0">
                    <td class="text-center" :colspan="dataTable.fields.length + 2">
                      {{$t('message.no_data')}}
                    </td>
                  </tr>
                  <tr v-for="item in dataTable.items" :key="item.id">
                    <td scope="row">
                      <c-switch type="icon" variant="success" v-bind="{on: '\uf00c', off: '\uf00d'}" :pill="true" v-model="item.active" :value="1" :uncheckedValue="0" @change="updateStatusItem(item)" />
                    </td>
                    <td scope="row" class="text-left" v-for="field in dataTable.fields" :key="field.id">
                      <span v-if="field.render != null"
                      v-html="field.render(item[field.name=='child_coupon_code'?'code':field.name], item)"></span>
                      <span v-if="field.render == null">
                        {{item[field.name]}}
                      </span>
                    </td>
                    <td scope="row" width="100">
                      <b-button size="sm" variant="primary" v-on:click="editExternalItem(item.uuid)">
                        <i class="fa fa-edit"></i> {{$t('common.edit')}}
                      </b-button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <!-- End of Data table session -->

              <!-- Start of paginate session -->
              <b-row>
                <b-col>
                  <b-pagination :total-rows="dataTable.total" :per-page="dataTable.perPage"
                  v-model="dataTable.currentPage" />
                </b-col>
                <b-col class="text-left">
                  {{$t('message.datatable_total', {number: dataTable.items.length, total: dataTable.total})}}
                </b-col>
                <b-col col lg="1">
                  <b-form-select v-model="dataTable.perPage" :options="[10, 20, 50, 100]" class="mb-3"/>
                </b-col>
              </b-row>
              <!-- Start of paginate session -->

            </b-col>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>
<style scoped>
  .table thead th:nth-child(1){
    width: 100px;
  }
</style>
<script>
import SelectDropdown from '../../../components/Partials/SelectDropdown'
import DataTable from '../../../components/Partials/DataTable'
import DateRangePicker from 'vue2-daterange-picker'
import DatePicker from 'vue2-datepicker'
import axios from 'axios'
import cSwitch from '../../../components/Switch'
const CancelToken = axios.CancelToken
let cancel = null
let vm = null
const FILLTER_KEY = 'ExternalListFilter'

export default {
  name: 'CouponList',
  components: {
    SelectDropdown,
    DateRangePicker,
    DatePicker,
    cSwitch,
    DataTable
  },
  data: function () {
    return {
      confirmText: {},
      languages: {},
      baseUrl: '#/coupon',
      type: 'like',
      date: [],
      statuses: [],
      auto_apply: [],
      couponTypes: [],
      filter: {
        options: [],
        selected: null,
        date: [],
        active: 1,
        coupon_type: "",
        auto_apply: "",
        name: '',
        code: '',
        field: null,
        key: null,
        startAt: this.$moment().format('YYYY-MM-DD'),
        endAt: "9999-12-31",
      },
      dataTable: {
        items: [],
        fields: [
          {
            name: 'coupon_type_id',
            filter: false,
            sort: false,
            render: (data, row) => {
              if (row['coupon']['coupon_type'] == null || row['coupon']['coupon_type'].name == null) {
                return ''
              }
              return row['coupon']['coupon_type'].name[this.$session.get('lang')]
            }
          },
          {
            name: 'child_coupon_code',
            filter: true,
            sort: false,
            render: (data, row) => {
              if (data == null) {
                return ''
              }
              return data
            }
          },
          {
            name: 'parent_coupon',
            filter: true,
            sort: false,
            render: (data, row) => {
              if (row['coupon'] == null) {
                return ''
              }
              return '<a href="#coupon/edit/'+row['coupon']['uuid']+'" target="_blank">'+row['coupon']['code']+'</a>'
            }
          },
          // {
          //   name: 'customer_id',
          //   filter: true,
          //   sort: false,
          //   render: (data, row) => {
          //     if (data === 0) {
          //       return ''
          //     }
          //     return '<a href="#customers/detail/'+data+'" target="_blank">'+data+'</a>'
          //   }
          // },
          {
            name: 'name',
            filter: true,
            sort: true,
            render: (data, row) => {
              if (row['coupon']['name'] == null) {
                return ''
              }
              return row['name'] ? row['name'][this.$session.get('lang')] : ''
            }
          },
          {
            name: 'applicable_period',
            filter: false,
            sort: false,
            render: (data, row) => {
              return row['apply_from'] + ' ~ ' + row['apply_to']
            }
          },
          {
            name: 'updated_by_email',
            filter: false,
            sort: false,
            render: (data, row) => {
              return data
            }
          },
          {
            name: 'updated_at',
            filter: false,
            sort: true,
            render: (data, row) => {
              if (data == null) {
                return ''
              }
              let datetime = this.convertUTCToClientTime(data)
              if (datetime != '') {
                return datetime.format(this.$store.getters.config.main.date_format ? this.$store.getters.config.main.date_format.toUpperCase() + ' HH:mm:ss' : 'DD/MM/YYYY HH:mm:ss')
              }
              return data
            }
          }
        ],
        sort: {
          field: 'id',
          type: 'desc'
        },
        currentPage: 1,
        total: 0,
        perPage: 20,
        source: null
      },
      item: {}
    }
  },
  methods: {
    onValidFilter () {
      vm.filter.startAt = this.$moment().format('YYYY-MM-DD')
      vm.filter.endAt = "9999-12-31"
      vm.filter.active = 1
      vm.refreshData()
    },
    updateDateRange (val) {
      vm.date = val
      vm.filter.startAt = val.startDate != null ? vm.$moment(val.startDate).format('YYYY-MM-DD') : vm.$moment().subtract(7, 'days').format('YYYY-MM-DD')
      vm.filter.endAt = val.endDate != null ? vm.$moment(val.endDate).format('YYYY-MM-DD') : "9999-12-31"
      vm.refreshData()
    },

    goToCouponDetail (uuid) {
      this.$router.push({name: 'coupon_edit', params: { id: uuid }})
    },
    /**
     * update sort field and sort type then refresh data
     * @param field
     */
    updateSort (field) {
      for (let i in this.dataTable.fields) {
        if (this.dataTable.fields[i].name == field
        && (this.dataTable.fields[i].sort == null || this.dataTable.fields[i].sort == false)) {
          return
        }
      }
      if (this.dataTable.sort.field != field) {
        this.dataTable.sort = {
          field: field,
          type: 'asc'
        }
      } else {
        if (this.dataTable.sort.type == 'asc') {
          this.dataTable.sort = {
            field: field,
            type: 'desc'
          }
        } else {
          this.dataTable.sort = {
            field: field,
            type: 'asc'
          }
        }
      }

      this.refreshData()
    },
    /**
     * refresh data for data table
     */
    refreshData () {
      if (cancel != null) {
        cancel()
      }

      //get params
      let _data = {
        page: this.dataTable.currentPage,
        limit: this.dataTable.perPage,
        sort_field: this.dataTable.sort.field,
        sort_type: this.dataTable.sort.type,
        start: this.filter.startAt,
        end: this.filter.endAt
      }
      let active = '';
      if (String(this.filter.active) != '') {
        _data['active'] = this.filter.active
      }
      if (String(this.filter.auto_apply) != '') {
        _data['auto_apply'] = this.filter.auto_apply
      }
      if (this.filter.coupon_type != '') {
        _data['coupon_type_id'] = this.filter.coupon_type
      }
      if (!this._.isEmpty(this.filter.field) && !this._.isEmpty(this.filter.key)) {
        if(this.filter.field == 'child_coupon_code') {
          this.filter.field = 'external_coupon_code';
        }
        if(this.filter.field == 'parent_coupon') {
          this.filter.field = 'web_code';
        }
        _data[this.filter.field] = this.filter.key.trim()
      }
      //process to send via get method
      let params = '';
      for (let key in _data) {
        params += key+'='+_data[key]+'&'
      }
      //remove tail
      params = params.slice(0, -1)
      params += '&is_external=1&customer_id=0&is_ges=0'
      vm.axios.coupon.getExternalCouponList(params,
        function(response){
          vm.dataTable.items = response.data.data.items
          vm.dataTable.total = response.data.data.pagination.total
        });
    },
    /**
     * move to detail page by uuid
     * @param uuid
     */
    editExternalItem (uuid) {
      this.preserveDataFilter(FILLTER_KEY, this.getDefaultDataFilter())
      this.$router.push({name: 'coupon_child_edit', params: { id: uuid }})
    },
    /**
     * update active by uuid
     * @param uuid and active
     */
    updateStatusItem (item) {
      let params = {
        uuid: item.uuid,
        active: item.active
      }
      vm.axios.coupon.updateExternalCouponStatus(params, function(response){
        vm.$toastr.success(vm.$t('message.update_successful'), vm.$t('message.success'), {})
        vm.refreshData()
      });
    },
    createChildCoupon: function(){
      this.$router.push({name: 'coupon_child_create', params: { id: 'create' }})
    },
    /**
     * get list categories for select box
     */
    getListCouponTypes () {
      vm.axios.coupon.getCouponTypies(function(response){
        vm.couponTypes = []
        let langs = vm.$store.getters.languages
        for (let l in langs) {
          vm.statuses[langs[l]] = [
            {text: vm.$t('common.all', langs[l]), value: ""},
            {text: vm.$t('common.active', langs[l]), value: '1'},
            {text: vm.$t('common.inactive', langs[l]), value: '0'}
          ]
          vm.auto_apply[langs[l]] = [
            {text: vm.$t('common.all', langs[l]), value: ""},
            {text: vm.$t('common.on', langs[l]), value: '1'},
            {text: vm.$t('common.off', langs[l]), value: '0'}
          ]
          vm.confirmText[langs[l]] = vm.$t('date_range.text_apply', langs[l]);
          vm.languages[langs[l]] = {
            'days': [
              vm.$t('date_range.sun', langs[l]),
              vm.$t('date_range.mon', langs[l]),
              vm.$t('date_range.tue', langs[l]),
              vm.$t('date_range.wed', langs[l]),
              vm.$t('date_range.thu', langs[l]),
              vm.$t('date_range.fri', langs[l]),
              vm.$t('date_range.sat', langs[l])
            ],
            'months': [
              vm.$t('date_range.month_1', langs[l]),
              vm.$t('date_range.month_2', langs[l]),
              vm.$t('date_range.month_3', langs[l]),
              vm.$t('date_range.month_4', langs[l]),
              vm.$t('date_range.month_5', langs[l]),
              vm.$t('date_range.month_6', langs[l]),
              vm.$t('date_range.month_7', langs[l]),
              vm.$t('date_range.month_8', langs[l]),
              vm.$t('date_range.month_9', langs[l]),
              vm.$t('date_range.month_10', langs[l]),
              vm.$t('date_range.month_11', langs[l]),
              vm.$t('date_range.month_12', langs[l])
            ],
            'pickers': [
              vm.$t('date_range.next_7_days', langs[l]),
              vm.$t('date_range.next_30_days', langs[l]),
              vm.$t('date_range.pre_7_days', langs[l]),
              vm.$t('date_range.pre_30_days', langs[l]),
            ],
            'placeholder': {
              'date': vm.$t('date_range.select_date', langs[l]),
              'dateRange': vm.$t('date_range.select_range', langs[l])
            }
          }
          vm.couponTypes[langs[l]] = [{
            text: vm.$t('common.all', langs[l]),
            value: ""
          }]
          
          for (let c in response.data.data.items) {
            vm.couponTypes[langs[l]].push({
              text: response.data.data.items[c]['name'][langs[l]] != null
              ? response.data.data.items[c]['name'][langs[l]] : '',
              value: response.data.data.items[c]['id']
            })
          }
        }
      })
    },
    transformDataField (val) {
      switch(val) {
        case 'external_coupon_code':
          return this.filter.selected = 'child_coupon_code'
        case 'web_code':
          return this.filter.selected = 'parent_coupon'
        default:
          return this.filter.selected = val
      }
    }
  },
  created: function () {
    vm = this
    let CancelToken = vm.axios.CancelToken
    vm.dataTable.source = CancelToken.source()

    for (let i in vm.dataTable.fields) {
      let field = vm.dataTable.fields[i].name
      if (vm.dataTable.fields[i].filter != null && vm.dataTable.fields[i].filter == true) {
        vm.filter.options.push(vm.dataTable.fields[i])
      }
      if (vm.dataTable.fields[i].sort != null && vm.dataTable.fields[i].sort == true
      && vm.dataTable.sort.field == null) {
        vm.dataTable.sort = {
          field: field,
          type: 'asc'
        }
      }
    }

    //added mets code into filter
    let skuItem = {
      name: 'sku',
      filter: true,
      sort: false
    }
    vm.filter.options.push(skuItem)

    vm.$Progress.start()
    vm.getListCouponTypes()
    vm.fetchDataFilter(FILLTER_KEY)
    vm.refreshData()
  },
  watch: {
    'watchMutiProperties': function (_) {
      vm.watchAndRefreshData(FILLTER_KEY, this.refreshData)
    },
    'filter.field': function (val) {
      vm.transformDataField(val)
    }
  },
  computed : {
    watchMutiProperties () {
      return `${this.filter.active}|${this.filter.auto_apply}|${this.filter.coupon_type}|${this.dataTable.currentPage}|${this.dataTable.perPage}`
    },
    date_locale : function () {
      let format = vm.format_date_range_locale()
      if(vm.$refs.drange){
        vm.$refs.drange.locale = format
      }
      return format
    },
    ranges : function () {
      return vm.format_date_ranges()
    },
  }
}
</script>



// WEBPACK FOOTER //
// src/views/admin/coupon/ExternalList.vue